import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { styled } from '@mui/material';
import { useInfiniteHits } from 'react-instantsearch';
import { useParams } from 'react-router-dom';

import { FILTERS } from '~/const';
import { useDiscoverContext } from '~/context/DiscoverContext';
import useInsightsMiddleware from '~/hooks/useInsightsMiddleware';
import usePrevious from '~/hooks/usePrevious';
import useSERPNavigation from '~/hooks/useSERPNavigation';
import useStats from '~/hooks/useStats';
import FiltersOverlay from '~/pages/DiscoverPage/components/FiltersOverlay/FiltersOverlay';
import FiltersPanel from '~/pages/DiscoverPage/components/FiltersPanel/FiltersPanel';
import ResultsPanel from '~/pages/DiscoverPage/components/ResultsPanel/ResultsPanel';
import { useIsWidthUp } from '~/theme';

interface SERPContainerProps {
  linkedInId?: string | null;
  attributesExcludedFromClear?: FILTERS[];
  loadingApiKey: boolean;
  userLoading: boolean;
  initialExpert?: string;
  initialCategory?: string;
  initialSkillId?: string;
}

export const SERPContainer: React.FC<SERPContainerProps> = ({
  linkedInId,
  attributesExcludedFromClear,
  loadingApiKey,
  userLoading,
  initialExpert,
  initialCategory,
  initialSkillId,
}) => {
  const isMobile = !useIsWidthUp('xl');
  const [loading, setLoading] = useState(true);
  const timeout = useRef<null | ReturnType<typeof setTimeout>>(null);

  const params = useParams();
  const isCategoryPage = !!params?.category;
  const { results } = useInfiniteHits();
  const { nbHits } = useStats();

  const type = useSERPNavigation();
  const previousSerpType = usePrevious(type);
  const resetTypeFilter = isCategoryPage || type === '';

  const { searchState } = useDiscoverContext();

  const status = useInsightsMiddleware({
    canCheckToken: Boolean(
      !loadingApiKey && process.env.REACT_APP_ALGOLIA_APP_NAME && !userLoading,
    ),
    allowedInsightMethods: ['clickedObjectIDsAfterSearch', 'clickedObjectIDs'],
    queryID: results?.queryID,
  });

  //used to detect initial loading for search page
  useEffect(() => {
    if (type !== previousSerpType) {
      setLoading(true);
    }
  }, [type, previousSerpType]);

  useEffect(() => {
    if (status === 'idle') {
      timeout.current = setTimeout(() => {
        setLoading(false);
      }, 100);
    } else {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    }
  }, [status]);

  //status change check is more reliable but when algolia has cached results it will not work
  useEffect(() => {
    if (nbHits > 0) {
      setLoading(false);
    }
  }, [nbHits]);

  return (
    <Wrapper>
      <FiltersOverlay attributesExcludedFromClear={attributesExcludedFromClear}>
        <FiltersPanel
          isMobile={isMobile}
          resetTypeFilter={resetTypeFilter}
          isSearching={searchState?.query}
          loading={loading || loadingApiKey}
          initialExpert={initialExpert}
          initialCategory={initialCategory}
          initialSkillId={initialSkillId}
        />
      </FiltersOverlay>
      <ResultsPanel loading={loading || loadingApiKey} linkedInId={linkedInId} />
    </Wrapper>
  );
};

export default SERPContainer;

const Wrapper = styled(Box)`
  display: flex;
`;
